import { useState } from 'react';
import {
  Alert,
  Button,
  Combobox,
  DatePicker,
  Divider,
  Field,
  GroupIcon,
  Input,
  PersonIcon,
  Spinner,
  Text,
  Textarea,
} from '@billda/design-system';
import { addYears, format, isSameMonth, parseISO, startOfDay } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { listGangs } from '../../../gangs/api/gangsApi';
import { useUsers } from '../../../users/hooks/useUsers';
import { useSitesSummary } from '../../../sites/hooks/useSitesSummary';
import { getDeductionPlots } from '../../api/deductionsAdminApi';
import { useCreateAdminDeduction } from '../../hooks/useCreateAdminDeduction';
import { useDeductionTargetPeriod } from '../../hooks/useDeductionTargetPeriod';

type DeductionTarget = 'gang' | 'worker';

/** Dropdowns show the full first page rather than a token handful. */
const OPTION_PAGE_SIZE = 100;

const NOTES_MAX_LENGTH = 1000;

interface CreateDeductionPanelContentProps {
  onSuccess: () => void;
  onCancel: () => void;
}

/** "21–27 Jul" when the week sits in one month, "28 Jul – 3 Aug" when it straddles two. */
function formatWeekRange(start: string, end: string): string {
  const from = parseISO(start);
  const to = parseISO(end);

  return isSameMonth(from, to)
    ? `${format(from, 'd')}–${format(to, 'd MMM')}`
    : `${format(from, 'd MMM')} – ${format(to, 'd MMM')}`;
}

/** Why a pot won't take another deduction, in the words the office would use. */
function describeLockedPot(potStatus: string | null | undefined): string {
  switch (potStatus) {
    case 'Submitted':
    case 'Submitting':
      return 'has already been submitted';
    case 'Approved':
      return 'has already been approved';
    case 'Paid':
      return 'has already been paid';
    default:
      return 'is closed';
  }
}

function SectionHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div>
      <Text variant="body2" className="font-bold">
        {title}
      </Text>
      {hint && (
        <Text variant="body2" color="secondary" className="mt-0.5">
          {hint}
        </Text>
      )}
    </div>
  );
}

export function CreateDeductionPanelContent({
  onSuccess,
  onCancel,
}: CreateDeductionPanelContentProps) {
  const today = startOfDay(new Date());

  const [target, setTarget] = useState<DeductionTarget>('gang');
  const [selectedGangId, setSelectedGangId] = useState<string | null>(null);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [selectedPlotId, setSelectedPlotId] = useState<number | null>(null);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [deductionDate, setDeductionDate] = useState<Date | null>(today);
  const [notes, setNotes] = useState('');

  // Gang options — first page, filtered client-side by the combobox
  const { data: gangsData } = useQuery({
    queryKey: ['gangs', 'search', ''],
    queryFn: () => listGangs('').then(r => r.gangs ?? []),
    staleTime: 60_000,
  });
  const gangs = gangsData ?? [];

  const { data: users = [], isFetching: usersLoading } = useUsers();

  // Site options — first page, filtered client-side by the combobox
  const { data: sites = [] } = useSitesSummary('');

  // Load all pages so client-side search includes every plot for the picked site.
  const { data: plots = [], isFetching: plotsLoading } = useQuery({
    queryKey: ['plots', 'deduction-panel', selectedSiteId, ''],
    queryFn: ({ signal }) => getDeductionPlots(selectedSiteId!, signal),
    enabled: !!selectedSiteId,
  });

  const { mutate: createDeduction, isPending } = useCreateAdminDeduction();

  const handleSiteChange = (siteId: string | number | undefined) => {
    setSelectedSiteId(typeof siteId === 'number' ? siteId : null);
    setSelectedPlotId(null);
  };

  const parsedAmount = parseFloat(amount);
  const amountNum = isNaN(parsedAmount) ? 0 : parsedAmount;
  const isDateInPast = !!deductionDate && deductionDate < today;

  // Ask the backend which week this date lands in, and whether that pot will take it
  const { data: targetPeriod, isFetching: targetPeriodLoading } =
    useDeductionTargetPeriod({
      date:
        deductionDate && !isDateInPast
          ? format(deductionDate, 'yyyy-MM-dd')
          : null,
      gangId: target === 'gang' ? selectedGangId : null,
      targetWorkerId: target === 'worker' ? selectedWorkerId : null,
    });

  const potIsLocked = targetPeriod?.canAddDeduction === false;

  const dateHelperText = (() => {
    if (isDateInPast) {
      return 'Pick today or a later date';
    }
    if (targetPeriodLoading) {
      return 'Checking which week…';
    }
    if (targetPeriod) {
      return `Comes out of the ${formatWeekRange(targetPeriod.periodStart, targetPeriod.periodEnd)} pot`;
    }
    return "Sets the week it's taken from";
  })();

  const isValid =
    (target === 'gang' ? !!selectedGangId : !!selectedWorkerId) &&
    description.trim().length >= 3 &&
    amountNum > 0 &&
    !!deductionDate &&
    !isDateInPast &&
    !potIsLocked &&
    notes.length <= NOTES_MAX_LENGTH;

  const handleSubmit = () => {
    createDeduction(
      {
        gangId: target === 'gang' ? selectedGangId : null,
        targetWorkerId: target === 'worker' ? selectedWorkerId : null,
        siteId: selectedSiteId,
        plotId: selectedPlotId,
        description: description.trim(),
        amount: amountNum,
        deductionDate: deductionDate
          ? format(deductionDate, 'yyyy-MM-dd')
          : null,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => onSuccess(),
      }
    );
  };

  const gangOptions = gangs
    .slice(0, OPTION_PAGE_SIZE)
    .map(g => ({ label: g.name, value: g.id }));

  const siteOptions = sites
    .slice(0, OPTION_PAGE_SIZE)
    .map(s => ({ label: s.name, value: s.id }));

  const plotOptions = plots.map(p => ({
    label: `${p.plotNumber} — ${p.name}`,
    value: p.id,
  }));

  const workerOptions = users.map(user => ({
    label: user.fullName || user.email,
    value: user.id,
  }));

  return (
    <div className="flex h-full flex-col">
      {/* Scrollable body */}
      <div className="flex-1 overflow-auto px-6 py-5">
        <div className="flex flex-col gap-6">
          {/* What's coming off */}
          <div className="flex flex-col gap-4">
            <SectionHeading title="What's coming off" />

            <Field label="Name" required hint={`${description.length}/200`}>
              <Input
                value={description}
                onChange={e => setDescription(e.target.value)}
                maxLength={200}
              />
            </Field>

            <div className="flex flex-col gap-4 sm:flex-row">
              <Field label="Amount" required className="flex-1">
                <Input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  min={0}
                  step="0.01"
                  startAdornment={<span>£</span>}
                />
              </Field>

              <Field
                label="Date"
                required
                className="flex-1"
                error={isDateInPast ? dateHelperText : undefined}
                hint={isDateInPast ? undefined : dateHelperText}
              >
                <DatePicker
                  value={deductionDate}
                  onChange={setDeductionDate}
                  minDate={today}
                  maxDate={addYears(today, 1)}
                />
              </Field>
            </div>
          </div>

          <Divider />

          {/* Who pays it */}
          <div className="flex flex-col gap-4">
            <SectionHeading
              title="Who pays it"
              hint="It comes out of their pot for the week you picked."
            />

            <div
              className="flex w-full"
              role="group"
              aria-label="Deduction target"
            >
              <Button
                variant={target === 'gang' ? 'neutral' : 'outline'}
                className="flex-1 rounded-r-none"
                aria-pressed={target === 'gang'}
                onClick={() => {
                  setTarget('gang');
                  setSelectedGangId(null);
                  setSelectedWorkerId(null);
                }}
              >
                <GroupIcon aria-hidden="true" className="size-4" />
                Gang
              </Button>
              <Button
                variant={target === 'worker' ? 'neutral' : 'outline'}
                className="flex-1 rounded-l-none"
                aria-pressed={target === 'worker'}
                onClick={() => {
                  setTarget('worker');
                  setSelectedGangId(null);
                  setSelectedWorkerId(null);
                }}
              >
                <PersonIcon aria-hidden="true" className="size-4" />
                Individual worker
              </Button>
            </div>

            {target === 'gang' && (
              <Field label="Gang" required>
                <Combobox
                  options={gangOptions}
                  value={selectedGangId ?? undefined}
                  onChange={value => {
                    setSelectedGangId(typeof value === 'string' ? value : null);
                  }}
                />
              </Field>
            )}

            {target === 'worker' && (
              <Field label="Worker" required>
                <Combobox
                  options={workerOptions}
                  loading={usersLoading}
                  value={selectedWorkerId ?? undefined}
                  onChange={value => {
                    setSelectedWorkerId(
                      typeof value === 'string' ? value : null
                    );
                  }}
                />
              </Field>
            )}

            {potIsLocked && targetPeriod && (
              <Alert severity="warning">
                The{' '}
                {formatWeekRange(
                  targetPeriod.periodStart,
                  targetPeriod.periodEnd
                )}{' '}
                pot {describeLockedPot(targetPeriod.potStatus)}, so nothing more
                can come out of it. Pick a later date.
              </Alert>
            )}
          </div>

          <Divider />

          {/* Where it came from */}
          <div className="flex flex-col gap-4">
            <SectionHeading
              title="Where it came from"
              hint="Optional. Ties the deduction to a site, and a plot if you know it."
            />

            <Field label="Site">
              <Combobox
                options={siteOptions}
                value={selectedSiteId ?? undefined}
                onChange={handleSiteChange}
              />
            </Field>

            <Field
              label="Plot"
              hint={selectedSiteId ? undefined : 'Pick a site first'}
            >
              <Combobox
                options={plotOptions}
                value={selectedPlotId ?? undefined}
                disabled={!selectedSiteId}
                loading={plotsLoading}
                onChange={value => {
                  setSelectedPlotId(typeof value === 'number' ? value : null);
                }}
              />
            </Field>
          </div>

          <Divider />

          {/* Notes */}
          <div className="flex flex-col gap-4">
            <SectionHeading
              title="Notes"
              hint="Office only — this doesn't show in the app."
            />

            <Field label="Notes" hint={`${notes.length}/${NOTES_MAX_LENGTH}`}>
              <Textarea
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                maxLength={NOTES_MAX_LENGTH}
                placeholder="Anything the office needs to know later"
              />
            </Field>
          </div>
        </div>
      </div>

      {/* Fixed footer */}
      <div className="border-t border-border-default bg-surface-default p-6">
        <div className="flex gap-4">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            variant="neutral"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
            fullWidth
          >
            {isPending && <Spinner size={20} color="inherit" />}
            {isPending ? 'Creating...' : 'Create deduction'}
          </Button>
        </div>
      </div>
    </div>
  );
}
